import { orderBy } from 'lodash'
import { useEffect, useState } from 'react'

import { Asset } from 'types'

import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSHeader from 'designSystem/components/PDSHeader/PDSHeader'
import PDSText from 'designSystem/components/PDSText/PDSText'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'
import { jsonClone } from 'utils/generalUtils'
import { searchFilter } from 'utils/searchFilter'

import PortfolioAssetListItem from './PortfolioAssetListItem'

type AssetSortType = 'total' | 'change' | 'ticker'

type Props = {
  initialAssets: Asset[]
  portfolioId: string
  search?: string
  sortField: AssetSortType
  title: string
  titleColor?: string
}

export default function BaseAssetList({
  initialAssets,
  portfolioId,
  search,
  sortField,
  title,
  titleColor = COLOR.textPrimary,
}: Props) {
  const [displayedAssets, setDisplayedAssets] = useState<Asset[]>(initialAssets)

  useEffect(() => {
    let newAssets = jsonClone(initialAssets) as Asset[]

    switch (sortField) {
      case 'total':
        newAssets = orderBy(
          newAssets,
          [(item) => (!item.totalValue ? -Infinity : item.totalValue)],
          'desc'
        )
        break
      case 'change':
        newAssets = orderBy(
          newAssets,
          [
            (item) =>
              !item?.priceDetails?.dayGain
                ? -Infinity
                : item.priceDetails.dayGain,
          ],
          'desc'
        )
        break
      case 'ticker':
        newAssets = orderBy(newAssets, 'symbol')
        break
    }

    const filteredAssets = newAssets.filter(
      (item) =>
        item?.shares > 0 &&
        (searchFilter(item, 'name', search ?? '') ||
          searchFilter(item, 'symbol', search ?? ''))
    )

    setDisplayedAssets(filteredAssets)
  }, [initialAssets, search, sortField])

  return (
    <PDSBox display="flex" flexDirection="column">
      <PDSFlexBox alignItems="center" justifyContent="space-between">
        <PDSFlexBox alignItems="center">
          <PDSHeader variant="h4" color={titleColor}>
            {title} ({displayedAssets.length})
          </PDSHeader>
        </PDSFlexBox>
      </PDSFlexBox>

      <PDSFlexBox flex={1} flexDirection="column">
        <PDSFlexBox
          flexDirection="column"
          height="100%"
          justifyContent="space-between"
        >
          <PDSBox>
            <PDSBox marginTop={SIZE.s12}>
              <PDSFlexBox flexDirection="column" gap={SIZE.s12}>
                <PDSFlexBox marginLeft={SIZE.s20} gap={SIZE.s8}>
                  <PDSBox width="26%">
                    <PDSBox>
                      <PDSText color={COLOR.textTertiary} weight="medium">
                        Asset
                      </PDSText>
                    </PDSBox>
                  </PDSBox>
                  <PDSFlexBox justifyContent="space-between" width="72%">
                    <PDSBox flexBasis="5rem">
                      <PDSText color={COLOR.textTertiary} weight="medium">
                        Price
                      </PDSText>
                    </PDSBox>

                    <PDSBox flexBasis="4.5rem">
                      <PDSText color={COLOR.textTertiary} weight="medium">
                        Quantity
                      </PDSText>
                    </PDSBox>

                    <PDSBox flexBasis="6rem">
                      <PDSText
                        color={COLOR.textTertiary}
                        weight="medium"
                        whiteSpace="nowrap"
                      >
                        Total Value
                      </PDSText>
                    </PDSBox>

                    <PDSBox flexBasis="4.5rem">
                      <PDSText
                        color={COLOR.textTertiary}
                        weight="medium"
                        whiteSpace="nowrap"
                      >
                        % Portfolio
                      </PDSText>
                    </PDSBox>

                    <PDSBox flexBasis="4rem">
                      <PDSText color={COLOR.textTertiary} weight="medium">
                        G/L
                      </PDSText>
                    </PDSBox>

                    <PDSBox flexBasis="5rem">
                      <PDSText
                        color={COLOR.textTertiary}
                        weight="medium"
                        whiteSpace="nowrap"
                      >
                        Day Gain
                      </PDSText>
                    </PDSBox>
                  </PDSFlexBox>
                </PDSFlexBox>
                {displayedAssets.map((a: Asset) => (
                  <PortfolioAssetListItem
                    key={a?.id ?? a?.symbol}
                    asset={a}
                    portfolioId={portfolioId}
                  />
                ))}
              </PDSFlexBox>
            </PDSBox>
          </PDSBox>
        </PDSFlexBox>
      </PDSFlexBox>
    </PDSBox>
  )
}
